import React, { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Star, Megaphone, Users, Flame, ArrowRight, ShoppingBag, Ticket, Check, Crown, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { TornEdge } from "../../components/sport/TornEdge";
import { useCart } from "../../lib/useCart";
import { toast } from "sonner";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

const MEMBERSHIPS = [
  { id: "fan", name: "Supporter Fan", price: 1500, icon: Heart, perks: ["Newsletter exclusive", "Forum supporters", "Réductions partenaires", "Tirages au sort"], color: "#ff4f81" },
  { id: "pasionnaire", name: "Passionnaire", price: 4500, icon: Flame, perks: ["Tout du pack Fan", "Place réservée tribune", "Accès meet & greet (1×/saison)", "Goodie de bienvenue", "Newsletter VIP"], color: SECONDARY, popular: true },
  { id: "ultra", name: "Ultra", price: 9900, icon: Crown, perks: ["Tout du pack Passionnaire", "Maillot officiel offert", "Accès vestiaires (1×/saison)", "Soirée privée fin de saison", "Photo équipe dédicacée"], color: PRIMARY },
];

const PRODUCTS = [
  { id: "echarpe", name: "Écharpe officielle", desc: "Polaire 150 cm, broderie blason", price: 1900, image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A2840-scaled-e1776959161196.jpg" },
  { id: "drapeau", name: "Drapeau supporter", desc: "150×100 cm, polyester double face", price: 2500, image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F5F6A9000-scaled.jpg" },
  { id: "mug", name: "Mug XL collector", desc: "Céramique 350ml, slogan #IPH", price: 1500, image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A4559-scaled.jpg" },
  { id: "bonbons", name: "Bonbons IPH", desc: "Sachet 200g goût acidulé", price: 600, image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A2181-scaled-e1777474281723.jpg" },
  { id: "puzzle", name: "Puzzle équipe", desc: "1000 pièces, photo équipe 25-26", price: 2900, image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A7467-scaled-e1773324091159.jpg" },
  { id: "casquette", name: "Casquette violet/ambre", desc: "Coton premium, broderie blason", price: 2500, image: "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F04%2F0J5A3051-scaled-e1775208468422.jpg" },
];

function priceFmt(c) { return `${(c / 100).toFixed(2)} €`; }

export default function SportSupporters() {
  const { add } = useCart();
  const [selectedMembership, setSelectedMembership] = useState(null);

  const addProduct = (p) => {
    add({
      id: `support-${p.id}`, slug: p.id, name: p.name,
      price_cents: p.price, featured_image: p.image,
      stock_tracking: false, stock_qty: 99,
    }, 1);
    toast.success(`${p.name} ajouté au panier`);
  };

  const joinMembership = (m) => {
    setSelectedMembership(m);
    add({
      id: `membership-${m.id}`, slug: `adhesion-${m.id}`, name: `Adhésion ${m.name} 2025-2026`,
      price_cents: m.price, featured_image: "",
      stock_tracking: false, stock_qty: 99,
    }, 1);
    toast.success(`Adhésion ${m.name} ajoutée au panier !`);
  };

  return (
    <div style={{ background: BG_DARK }}>
      {/* HERO */}
      <section className="relative pt-32 pb-12 sm:pt-40 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${BG_DARK} 0%, #ff4f81 50%, ${SECONDARY} 100%)` }}/>
        <div className="absolute inset-0 opacity-25 pointer-events-none" style={{ background: `radial-gradient(ellipse at top right, ${SECONDARY}, transparent 50%), radial-gradient(ellipse at bottom left, #ff4f81, transparent 60%)` }}/>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2220%22 height=%2220%22 viewBox=%220 0 20 20%22><path d=%22M10 1.5l2.6 5.3 5.9.9-4.2 4.1 1 5.8L10 14.9l-5.3 2.7 1-5.8-4.2-4.1 5.9-.9z%22 fill=%22%23ffffff%22 fill-opacity=%220.04%22/></svg>')]"/>
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-2xl mb-4">
            <Heart size={36} fill="#ff4f81" className="text-pink-500"/>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-white font-black tracking-[0.4em] text-xs uppercase mb-3">
            Espace supporters
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 0.95 }}>
            Bienvenue dans la <span className="text-yellow-200" style={{ filter: "drop-shadow(0 4px 16px rgba(255,255,255,0.4))" }}>tribu</span>
          </motion.h1>
          <p className="mt-4 text-white/95 max-w-2xl mx-auto text-base font-medium drop-shadow">Le 8e joueur, c'est vous. Rejoignez la communauté la plus chaude de la StarLigue, équipez-vous et faites trembler les parquets.</p>
        </div>
        <TornEdge position="bottom" color="#fbf7fa" variant="rip" height={64}/>
      </section>

      {/* MEMBERSHIPS */}
      <section className="relative py-20" style={{ background: "#fbf7fa" }}>
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-pink-600 font-black tracking-[0.3em] text-xs uppercase mb-2">Le club des supporters</div>
            <h2 className="font-black text-3xl sm:text-4xl uppercase italic" style={{ color: PRIMARY }}>
              Choisissez votre <span style={{ color: SECONDARY }}>adhésion</span>
            </h2>
            <p className="text-slate-500 text-sm mt-3">Rejoignez l'amicale officielle des supporters et bénéficiez d'avantages exclusifs.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {MEMBERSHIPS.map((m, i) => (
              <motion.div key={m.id}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                data-testid={`membership-${m.id}`}
                className={`relative rounded-3xl overflow-hidden shadow-2xl ${m.popular ? "ring-4 ring-pink-500 lg:scale-105" : ""}`}>
                {m.popular && (
                  <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-pink-500 text-black text-[10px] font-black uppercase tracking-widest shadow-lg">
                    <Star size={10} fill="black"/> Populaire
                  </div>
                )}
                <div className="px-6 py-8 text-center" style={{ background: `linear-gradient(135deg, ${m.color}, ${PRIMARY})` }}>
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/95 mb-3 shadow-xl">
                    <m.icon size={28} className="text-[var(--c)]" style={{ "--c": m.color }}/>
                  </div>
                  <h3 className="text-white font-black text-xl uppercase italic">{m.name}</h3>
                  <div className="mt-4 text-white">
                    <span className="text-5xl font-black">{(m.price / 100).toFixed(0)}<span className="text-2xl">€</span></span>
                    <div className="text-white/80 text-[11px] font-bold uppercase tracking-widest mt-1">/ saison</div>
                  </div>
                </div>
                <div className="p-6 bg-white">
                  <ul className="space-y-2.5">
                    {m.perks.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check size={15} className="text-emerald-500 mt-0.5 shrink-0"/>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`join-${m.id}`}
                    onClick={() => joinMembership(m)}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-black uppercase text-xs tracking-widest shadow-lg hover:scale-[1.02] transition"
                    style={{ background: `linear-gradient(135deg, ${m.color}, ${PRIMARY})`, color: "white" }}>
                    Adhérer <ArrowRight size={14}/>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTS GRID */}
      <section className="relative py-20" style={{ background: BG_DEEP }}>
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-pink-400 font-black tracking-[0.3em] text-xs uppercase mb-2">Boutique supporters</div>
              <h2 className="text-white font-black text-3xl sm:text-4xl uppercase italic">
                Équipez-vous pour <span style={{ color: SECONDARY }}>vibrer</span>
              </h2>
            </div>
            <Link to="/sport/boutique" data-testid="link-boutique-jersey"
              className="inline-flex items-center gap-2 text-pink-400 font-black uppercase text-xs tracking-widest hover:text-pink-300">
              Maillot personnalisé <ArrowRight size={14}/>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRODUCTS.map((p, i) => (
              <motion.div key={p.id}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-pink-500/50 transition group"
                data-testid={`product-${p.id}`}>
                <div className="relative aspect-square overflow-hidden">
                  <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-700"/>
                  <div className="absolute top-3 right-3 inline-block px-2.5 py-1 rounded-full bg-black/70 backdrop-blur text-pink-400 text-[10px] font-black uppercase tracking-widest">
                    {priceFmt(p.price)}
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-white font-black uppercase text-sm">{p.name}</h3>
                  <p className="text-white/60 text-[12px] mt-1">{p.desc}</p>
                  <button data-testid={`add-${p.id}`}
                    onClick={() => addProduct(p)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-black uppercase text-[11px] tracking-widest hover:scale-[1.02] transition"
                    style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: BG_DARK }}>
                    <ShoppingBag size={13}/> Ajouter au panier
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY CTA */}
      <section className="relative py-20" style={{ background: BG_DARK }}>
        <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center">
          <Megaphone className="w-12 h-12 text-pink-400 mx-auto mb-4"/>
          <h2 className="text-white font-black text-3xl uppercase italic">
            Rejoignez le <span style={{ color: SECONDARY }}>groupe officiel</span>
          </h2>
          <p className="text-white/70 mt-3 max-w-xl mx-auto">Tirages, déplacements organisés, animations tribunes… retrouvez la communauté la plus enflammée de la StarLigue.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="https://www.facebook.com/" target="_blank" rel="noopener noreferrer"
              className="px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest text-white hover:scale-105 transition" style={{ background: "#1877F2" }}>
              Groupe Facebook
            </a>
            <a href="https://www.instagram.com/" target="_blank" rel="noopener noreferrer"
              className="px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest text-white hover:scale-105 transition" style={{ background: "linear-gradient(135deg, #fa7e1e, #d62976, #962fbf)" }}>
              Compte Instagram
            </a>
            <a href="mailto:supporters@istreshandball.com" className="px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest border-2 border-white/30 text-white hover:bg-white hover:text-black transition">
              <Mail size={13} className="inline mr-1"/> Nous écrire
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
